import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../api";
import { useCart } from "../contexts";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", txn: null, attempts: 0 });
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", txn: null, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const t = r.data;
        if (t.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          setState({ status: "paid", txn: t, attempts });
        } else if (t.status === "expired" || t.payment_status === "failed") {
          setState({ status: "failed", txn: t, attempts });
        } else if (attempts >= 8) {
          setState({ status: "timeout", txn: t, attempts });
        } else {
          setState({ status: "pending", txn: t, attempts });
          setTimeout(poll, 2000);
        }
      } catch {
        setState((s) => ({ ...s, status: "error", attempts }));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="max-w-[700px] mx-auto px-4 sm:px-6 py-20 text-center pb-32" data-testid="checkout-success-page">
      {state.status === "loading" || state.status === "pending" ? (
        <>
          <Loader2 className="mx-auto animate-spin text-[var(--accent)]" size={42} />
          <h1 className="font-display text-4xl uppercase mt-6">Confirming your payment…</h1>
          <p className="font-mono-label text-[11px] text-neutral-500 mt-3">Hold tight. Verifying with Stripe.</p>
        </>
      ) : state.status === "paid" ? (
        <>
          <CheckCircle2 className="mx-auto text-[var(--acid)]" size={56} />
          <h1 className="font-display text-5xl uppercase mt-6" data-testid="success-title">Order Confirmed</h1>
          <p className="text-neutral-300 mt-4">
            Paid <span className="font-headline">${state.txn?.amount?.toFixed?.(2) || state.txn?.amount}</span>. A receipt will hit your inbox shortly.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/orders" className="btn-primary" data-testid="success-orders-link">View My Orders</Link>
            <Link to="/" className="btn-ghost">Back Home</Link>
          </div>
        </>
      ) : state.status === "missing" ? (
        <>
          <AlertTriangle className="mx-auto text-[var(--accent)]" size={42} />
          <h1 className="font-display text-4xl uppercase mt-6">Missing session</h1>
          <Link to="/" className="btn-ghost mt-6">Home</Link>
        </>
      ) : (
        <>
          <AlertTriangle className="mx-auto text-[var(--accent)]" size={42} />
          <h1 className="font-display text-4xl uppercase mt-6" data-testid="success-error-title">Payment not confirmed</h1>
          <p className="text-neutral-400 mt-3">{state.status === "timeout" ? "Verification timed out. Check Orders page in a moment." : "Something went wrong."}</p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/orders" className="btn-ghost">Orders</Link>
            <Link to="/cart" className="btn-ghost">Cart</Link>
          </div>
        </>
      )}
    </div>
  );
}
